// Libraries
import { Link } from "@/i18n/navigation";
import moment from "moment";
import Image from "next/image";
import Ellipsis from "../ui/Ellipsis/Ellipsis";
import { useLocale, useTranslations } from "next-intl";
import { capitalize } from "@/helpers/capitalize";
import { articleCoverPath } from "@/helpers/articleCover";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function ArticleCard({ empty, skeleton, ...props }) {
    // Variables
    const { article } = props;
    let dateCreation;
    let link;
    const locale = useLocale();
    const sess = props.session ? props.session.user.timezone : "Europe/Paris";
    const t = useTranslations();

    if (!empty && !skeleton) {
        if (props.admin) {
            link = `/admin/articles/${article.slug}`;
            dateCreation = moment(article.submissionDate)
                .locale(locale)
                .calendar();
        } else if (props.writer) {
            link = `/writer/articles/${article.slug}`;
        } else {
            link = `/r/${article.slug}`;
            dateCreation = moment(article.dateCreation)
                .locale(locale)
                .calendar();
        }
    }

    if (empty) {
        return (
            <div className="group bg-white dark:bg-grayDark-900 rounded-lg overflow-hidden border border-gray-200 dark:border-grayDark-700 h-full shadow-sm hover:shadow-md transition-all duration-200">
                <div className="flex flex-col justify-between h-full">
                    <div className="flex-grow flex flex-col">
                        {/* Cover */}
                        <div className="relative aspect-w-16 aspect-h-9 h-48 overflow-hidden">
                            <Image
                                src="/images/site/courses/cover-progress.jpg"
                                alt="Believemy"
                                width={500}
                                height={300}
                                className="object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                        </div>
                        <div className="p-5 flex flex-col flex-grow">
                            {/* Title */}
                            <div className="flex-grow">
                                <h3 className="font-semibold text-gray-900 dark:text-grayDark-100 text-lg leading-tight">
                                    {t("ArticleCard_soon_title")}
                                </h3>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (skeleton) {
        return (
            <div className="bg-white dark:bg-grayDark-900 rounded-lg p-5 overflow-hidden border border-gray-200 dark:border-grayDark-700 h-full shadow-sm">
                <div className="flex flex-col justify-between h-full">
                    <div className="flex-grow flex flex-col">
                        {/* Cover */}
                        <div className="aspect-w-16 aspect-h-9 h-48 rounded-md overflow-hidden">
                            <Skeleton
                                width="100%"
                                height="100%"
                                className="rounded-md"
                            />
                        </div>
                        <div className="p-5 flex flex-col flex-grow">
                            {/* Title */}
                            <div className="flex-grow">
                                <Skeleton count={2} className="rounded mb-2" />
                                <Skeleton width="70%" className="rounded" />
                            </div>

                            {/* Date */}
                            <div className="mt-4">
                                <Skeleton
                                    width={100}
                                    height={20}
                                    className="rounded-full"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Link href={link}>
            <Card className="group bg-white dark:bg-bgDark-840 rounded-lg overflow-hidden border border-gray-200 dark:border-grayDark-700 hover:border-gray-300 dark:hover:border-grayDark-600 hover:shadow-lg transition-all duration-200 h-full flex flex-col">
                <div
                    key={`article-${props.index || props.key}`}
                    className="h-full"
                >
                    <div className="flex flex-col justify-between h-full">
                        <div className="flex-grow flex flex-col">
                            {/* Cover */}
                            <div className="relative aspect-w-16 aspect-h-9 h-48 overflow-hidden">
                                {/* Sa vignette, ou celle qu'on dessine a sa
                                    place. Sans ce repli, un article sans
                                    couverture passait `null` a next/image, qui
                                    leve « missing required src property » et
                                    fait tomber toute la carte. */}
                                <Image
                                    src={articleCoverPath(article, locale)}
                                    alt={article.title}
                                    layout="fill"
                                    unoptimized={!article.cover}
                                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                                <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                            </div>

                            <CardHeader className="flex-grow p-5 pb-3">
                                <div className="flex flex-col">
                                    {/* Title */}
                                    <CardTitle className="leading-relaxed flex-grow text-lg font-semibold text-gray-900 dark:text-grayDark-100 group-hover:text-gray-700 dark:group-hover:text-grayDark-200 transition-colors duration-200">
                                        <Ellipsis maxLines="3" disableReadMore>
                                            {article.title}
                                        </Ellipsis>
                                    </CardTitle>
                                </div>
                            </CardHeader>

                            <CardContent className="px-5 pb-5 self-end">
                                {/* Date */}
                                <div>
                                    {props.admin ? (
                                        <small className="flex items-center gap-1.5 text-gray-500 dark:text-grayDark-400 group-hover:text-gray-600 dark:group-hover:text-grayDark-300 transition-colors duration-200">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-3.5 w-3.5"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                />
                                            </svg>
                                            {capitalize(dateCreation)}
                                        </small>
                                    ) : (
                                        <Badge
                                            variant="outline"
                                            className="text-gray-600 dark:text-grayDark-400 border-gray-300 dark:border-grayDark-600 hover:bg-gray-50 dark:hover:bg-grayDark-800 transition-colors duration-200 font-medium"
                                        >
                                            {capitalize(
                                                moment
                                                    .utc(article.dateCreation)
                                                    .locale(locale)
                                                    .tz(sess)
                                                    .fromNow(),
                                            )}
                                        </Badge>
                                    )}
                                </div>
                            </CardContent>
                        </div>
                    </div>
                </div>
            </Card>
        </Link>
    );
}
